import ShippingMethodResource from "@/module/Form/Resources/ShippingMethodResource";
import NumberHelper from "@/module/NumberHelper";
import FormHelper from "@/module/FormHelper";
import FormItem from "@/module/FormItem";
import ExchangeRateProvider from "@/module/ExchangeRate/ExchangeRateProvider";
import { ItemType } from "@/module/Item/ItemType";
import FormItemPrice from "@/module/FormItemPrice";

export default class FormShippingItem {
  readonly shippingMethodResource: ShippingMethodResource;
  prices: { [key: string]: number } = {};
  formItems: FormItem[] = [];
  freeOfCharges: { [key: string]: number } = {};

  constructor(shippingMethodResource: ShippingMethodResource) {
    this.shippingMethodResource = shippingMethodResource;
  }

  async updatePrices(
    currency: string,
    domesticCurrency: string,
    exchangeRateProvider: ExchangeRateProvider,
    vatRate: number,
  ) {
    const prices = await this.process(
      currency,
      domesticCurrency,
      exchangeRateProvider,
    );
    const formItems = await this.createItems(
      prices,
      currency,
      vatRate,
      exchangeRateProvider,
    );
    const freeOfCharges = await this.createFreeOfCharges();

    this.prices = prices;
    this.formItems = formItems;
    this.freeOfCharges = freeOfCharges;
  }

  private async process(
    currency: string,
    domesticCurrency: string,
    exchangeRateProvider: ExchangeRateProvider,
  ): Promise<{ [key: string]: number }> {
    const prices = {};

    for (const addressPrice of this.shippingMethodResource.address_prices) {
      const price = this.findAddressPrice(addressPrice.prices, currency);

      if (price !== undefined) {
        FormShippingItem.addPrice(
          prices,
          this.getKeyByType(addressPrice),
          price.price,
        );

        continue;
      }

      const domesticAddressPrice = this.findAddressPrice(
        addressPrice.prices,
        domesticCurrency,
      );

      let domesticPrice = 0;

      if (domesticAddressPrice) {
        domesticPrice = domesticAddressPrice.price;
      } else if (addressPrice.prices !== undefined) {
        let firstPriceObject: {
          currency_code: string;
          price: number;
        } | null = null;

        for (const tmpPrice of addressPrice.prices) {
          firstPriceObject = tmpPrice;
          break;
        }

        if (firstPriceObject === null) {
          throw new Error("Invalid shipping item data.");
        }

        domesticPrice = (await FormHelper.toDomesticCurrency(
          firstPriceObject.price,
          firstPriceObject.currency_code,
          exchangeRateProvider,
          true,
        )) as number;
      }

      if (currency === domesticCurrency) {
        FormShippingItem.addPrice(
          prices,
          this.getKeyByType(addressPrice),
          domesticPrice,
        );

        continue;
      }

      FormShippingItem.addPrice(
        prices,
        this.getKeyByType(addressPrice),
        FormHelper.toSourceCurrency(
          domesticPrice,
          currency,
          exchangeRateProvider,
        ) as number,
      );
    }

    return prices;
  }

  private static addPrice(
    prices: { [key: string]: number },
    key: string,
    price: number,
  ): void {
    prices[key] = NumberHelper.round(price);
  }

  private findAddressPrice(
    addressPrices: { currency_code: string; price: number }[] | undefined,
    currency: string,
  ) {
    if (addressPrices === undefined) {
      return undefined;
    }

    return addressPrices.find((price) => {
      return price.currency_code === currency;
    });
  }

  private async createItems(
    prices: { [key: string]: number },
    currency: string,
    vatRate: number,
    exchangeRateProvider: ExchangeRateProvider,
  ): Promise<Array<FormItem>> {
    const formItems = [];

    for (const name in prices) {
      const price = prices[name];

      const formItem = new FormItem(
        0,
        0,
        [
          new FormItemPrice({
            type: "one_time",
            price: price,
            currency_code: currency,
            metadata: null,
            repayment_count: null,
          }),
        ],
        this.shippingMethodResource.including_vat,
        0,
        name,
        null,
        null,
        false,
        true,
        ItemType.SHIPPING,
        false,
        false,
        false,
        null,
        false,
        false,
        null,
        null,
        this.shippingMethodResource.meta_data,
        null,
        null,
      );

      formItems.push(formItem);
      await formItem.recalculate(
        vatRate,
        currency,
        currency,
        exchangeRateProvider,
        false,
        false,
      );
    }

    return formItems;
  }

  private async createFreeOfCharges(): Promise<{ [key: string]: number }> {
    if (this.shippingMethodResource.free_of_charges === null) {
      return { noFreeShipping: -1 };
    }

    const freeOfChargesJSON = JSON.parse(
      this.shippingMethodResource.free_of_charges.toString(),
    );

    if (freeOfChargesJSON.length === 0) {
      return { noFreeShipping: -1 };
    }

    const freeOfCharges = {};

    for (const freeOfCharge of freeOfChargesJSON) {
      FormShippingItem.addPrice(
        freeOfCharges,
        freeOfCharge.currency_code,
        freeOfCharge.price,
      );
    }

    return freeOfCharges;
  }

  private getKeyByType(addressPrice: {
    address: { simple_address?: string; country_code?: string };
  }): string {
    if (this.shippingMethodResource.type === "personal_collection") {
      return addressPrice.address.simple_address as string;
    }

    return addressPrice.address.country_code as string;
  }
}
